/**
 * Gemini Client: Sends prompt to Google Gemini API
 * Securely handles AI requests via Cloudflare Functions (API key stays server-side)
 */

export async function sendToGemini(promptText) {
  // Use Cloudflare Function instead of calling Gemini directly
  const endpoint = "/api/gemini";
  console.log("🤖 GEMINI - Sending request with prompt length:", promptText.length);

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      prompt: promptText,
      model: 'gemini-2.5-flash'
    }),
  });

  if (!response.ok) {
    const errorDetails = await response.text();
    console.error("🤖 GEMINI ERROR:", errorDetails);
    throw new Error(`Failed to connect to Gemini API: ${response.status} - ${errorDetails}`);
  }

  const result = await response.json();
  console.log("🤖 GEMINI SUCCESS - Response received, length:", result.candidates?.[0]?.content?.parts?.[0]?.text?.length);
  return result.candidates?.[0]?.content?.parts?.[0]?.text || "";
}

// Step 1: Upload PDF to Gemini File API via Cloudflare Function
export async function uploadPDFToGemini(file) {
  const endpoint = "/api/gemini-upload";

  // Create FormData for multipart upload
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(endpoint, {
    method: "POST",
    body: formData
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Gemini upload error:", errorText);
    throw new Error(`Gemini upload failed: ${errorText}`);
  }

  const data = await response.json();
  console.log("Gemini upload API response:", data);

  // Wait for file to be processed and return full URI
  const fileUri = data.file?.name;
  if (!fileUri) {
    throw new Error("No file URI returned from Gemini upload");
  }

  // Note: File processing wait is handled server-side
  // For now, we'll add a small delay
  await new Promise(resolve => setTimeout(resolve, 3000));

  return fileUri;
}

// Step 2: Send prompt and file_id to Gemini via Cloudflare Function
export async function sendToGeminiWithFile(promptText, file) {
  const endpoint = "/api/gemini-with-file";
  try {
    // Upload PDF and get file URI
    const fileUri = await uploadPDFToGemini(file);
    console.log("File URI returned from Gemini:", fileUri);

    console.log("Sending prompt and file URI to Cloudflare Function");

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prompt: promptText,
        fileUri: fileUri,
        model: 'gemini-2.5-flash'
      }),
    });

    if (!response.ok) {
      const errMsg = await response.text();
      console.error("Gemini processing error:", errMsg);
      throw new Error(`Failed to process PDF with Gemini: ${errMsg}`);
    }

    const result = await response.json();
    console.log("Gemini processing response:", result);
    return result.candidates?.[0]?.content?.parts?.[0]?.text || "";

  } catch (error) {
    console.error("Error in sendToGeminiWithFile:", error);
    throw error;
  }
}
